"use client";

import { Transition } from "@headlessui/react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useRef, useState } from "react";
import { formatBytes } from "../../formatters/formatBytes";
import { DeleteIcon } from "../../icons/DeleteIcon";
import { FileIcon } from "../../icons/FileIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { Text } from "../../typography/Text";
import { IconButton } from "../Button";
import { FormError } from "../FormError";
import { FileItemPreview } from "./FileItemPreview";
import { FileName } from "./FileName";
import type { FileUploadFile } from "./types";

const removeFileLabel = msg({ message: "Remove File" });

export type FileItemProps = {
	file: FileUploadFile;
	onRemoveFile?: (key: string) => void;
	showFilePreview?: boolean;
	isInteractive?: boolean;
	allowDownload?: boolean;
};

export const FileItem = ({ file, onRemoveFile, showFilePreview, isInteractive, allowDownload }: FileItemProps) => {
	const { i18n } = useLingui();
	const [showProgress, setShowProgress] = useState(
		typeof file.progress === "number" && file.progress < 100 && !file.error,
	);

	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let id: number;
		if (file.progress === 100) {
			id = window.setTimeout(() => setShowProgress(false), 2000);
		} else if (!file.error && typeof file.progress === "number") {
			setShowProgress(true);
		}

		return () => {
			if (id) {
				window.clearTimeout(id);
			}
		};
	}, [file.progress, file.error]);

	const openTooltipOnOverflow = () => {
		if ((ref.current?.offsetWidth ?? 0) < (ref.current?.scrollWidth ?? 0)) {
			setIsOpen(true);
		}
	};

	if (!file.error && !showProgress && showFilePreview) {
		return (
			<Tooltip open={isOpen}>
				<TooltipTrigger>
					<div className="my-4">
						<div className="rounded-lg border border-neutral-faded p-4">
							<div className="mb-2 flex items-center justify-between">
								<div
									className="truncate"
									ref={ref}
									onMouseEnter={openTooltipOnOverflow}
									onMouseLeave={() => setIsOpen(false)}
								>
									<FileName url={file.url} name={file.name} allowDownload={allowDownload} />
								</div>
								<div className="flex items-center gap-4">
									{file.size && (
										<Text className="whitespace-nowrap" intent="muted" size="xs">
											{formatBytes(file.size)}
										</Text>
									)}
									{isInteractive && (
										<IconButton
											onClick={() => onRemoveFile?.(file.key)}
											aria-label={i18n._(removeFileLabel)}
											size="xs"
											intent={"secondary"}
											icon={DeleteIcon}
											isDestructive
											tooltip={false}
										/>
									)}
								</div>
							</div>
							<FileItemPreview url={file.url} name={file.name} type={file.type} />
						</div>
					</div>
				</TooltipTrigger>
				<TooltipContent>{file.name}</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<Tooltip open={isOpen}>
			<TooltipTrigger>
				<div className="flex min-w-0 flex-col gap-2 rounded-lg border border-neutral-faded bg-white p-2">
					<div className="flex w-full min-w-0 items-center justify-between">
						<div className="flex min-w-0 items-center gap-2">
							<FileIcon className="h-5 min-h-5 w-5 min-w-5" />
							<div
								className="flex min-w-0"
								ref={ref}
								onMouseEnter={openTooltipOnOverflow}
								onMouseLeave={() => setIsOpen(false)}
							>
								<FileName url={file.url} name={file.name} allowDownload={allowDownload} />
							</div>
							{file.size && (
								<Text className="whitespace-nowrap" intent="muted" size="sm">
									({formatBytes(file.size)})
								</Text>
							)}
						</div>
						<div className="flex items-center gap-4">
							{isInteractive && (
								<IconButton
									onClick={() => onRemoveFile?.(file.key)}
									aria-label={i18n._(removeFileLabel)}
									size="xs"
									intent={"tertiary"}
									icon={DeleteIcon}
									isDestructive
									tooltip={false}
								/>
							)}
						</div>
					</div>

					<Transition
						as="div"
						data-testid="file-upload-progress"
						show={showProgress}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<div className="w-full rounded-full bg-neutral-faded">
							<div
								className="h-1.5 rounded-full bg-primary text-center font-medium text-white text-xs leading-none"
								style={{
									width: `${file.progress}%`,
								}}
							/>
						</div>
					</Transition>

					<Transition
						as="div"
						show={!!file.error}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<FormError error={file.error!} />
					</Transition>
				</div>
			</TooltipTrigger>
			<TooltipContent>{file.name}</TooltipContent>
		</Tooltip>
	);
};
